import { Flex, Stack, Text } from '@mantine/core'
import { Dropzone } from '@mantine/dropzone'
import { IconFileTypeDocx } from '@tabler/icons-react'
import { useEffect, useState } from 'react'
import { updatePublishedChapter } from 'src/apis/bookAPI'
import { uploadDocFile } from 'src/apis/uploadApi'
import {
	WordFilePartialResult,
	WordFileRefusalAlert,
	WordFileReplaceNotice,
} from 'src/components/BookUniVini/WordFileNotices'
import CustomButton from 'src/components/CustomComponents/CustomButton'
import CustomModal from 'src/components/CustomComponents/CustomModal/CustomModal'
import CustomProgress from 'src/components/CustomComponents/CustomProgress'
import { useModal } from 'src/context/ModalContext'
import { BookData, ChapterData } from 'src/interfaces'
import {
	TextFixRefusal,
	TextFixResult,
	describeTextFixResult,
	getChapterWordFile,
	getTextFixRefusal,
	getTextFixResult,
	isTextFixPartial,
} from 'src/utils/chapterWordFile'
import { DOCX_DROPZONE_ACCEPT, pickDocWordFiles } from 'src/utils/docxDropzone'

// UD-614: replacing the English Word file of one chapter. The CMS has ONE way to do it (the
// owner's order): the server swaps the file and puts the words it changes into the English
// text, the chapter stays published, audio and translations are kept, nothing is charged. The
// server refuses a file with another number of pages or paragraphs; this dialog then says why
// in one sentence and what to do instead (delete the book and upload it again). When the server
// could not place some of the changes, the dialog stays open and says how many: staff are never
// told only "replaced" when something was left out. Nothing here can start the paid redo of
// translations and audio.

interface ReplaceWordFileDialogProps {
	opened: boolean
	onClose: () => void
	book: BookData | null
	chapter: ChapterData | null
	onReplaced?: () => void | Promise<void>
}

type UploadedFile = { url: string; name: string }

function ReplaceWordFileDialog({
	opened,
	onClose,
	book,
	chapter,
	onReplaced,
}: ReplaceWordFileDialogProps) {
	const { openError, openModal } = useModal()
	const [uploaded, setUploaded] = useState<UploadedFile | null>(null)
	const [progress, setProgress] = useState(0)
	const [uploading, setUploading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [refusal, setRefusal] = useState<TextFixRefusal | null>(null)
	const [partial, setPartial] = useState<TextFixResult | null>(null)

	useEffect(() => {
		if (!opened) return
		setUploaded(null)
		setProgress(0)
		setUploading(false)
		setSaving(false)
		setRefusal(null)
		setPartial(null)
	}, [opened, chapter?.id])

	const current = getChapterWordFile(chapter, book)
	const number = chapter?.chapter_number

	const handleUpload = async (files: File[]) => {
		const file = pickDocWordFiles(files)[0]
		if (!file) {
			openError(new Error('Only .doc or .docx files are allowed'))
			return
		}

		setRefusal(null)
		setUploaded(null)
		setProgress(0)
		setUploading(true)
		try {
			const url = await uploadDocFile(file, (event) => {
				const { loaded, total } = event
				if (total) setProgress(Math.round((loaded * 100) / total))
			})
			if (!url) {
				openError(new Error('Upload failed, no file address returned'))
				return
			}
			// The name the file has on this computer is sent with it, so it can be shown and
			// used again when the file is downloaded.
			setUploaded({ url, name: file.name })
		} catch (error) {
			openError(error)
		} finally {
			setUploading(false)
		}
	}

	const handleReplace = async () => {
		if (!chapter?.id || !uploaded) return

		setSaving(true)
		try {
			const res = await updatePublishedChapter(chapter.id, {
				doc_url: uploaded.url,
				doc_file_name: uploaded.name,
				replace_mode: 'text_fix',
			})
			const result = getTextFixResult(res)

			await onReplaced?.()
			if (isTextFixPartial(result)) {
				// The file is replaced, but not everything it changes is in the English text:
				// that is said here, in the open dialog, until staff close it.
				setPartial(result)
				return
			}
			openModal({ type: 'success', label: describeTextFixResult(result) })
			onClose()
		} catch (error) {
			const refused = getTextFixRefusal(error)
			if (refused) {
				// Nothing was changed on the server: the reason and the advice are shown here
				setRefusal(refused)
			} else {
				openError(error)
			}
		} finally {
			setSaving(false)
		}
	}

	// A refused file is not sent again: another file has to be picked first
	const canReplace = Boolean(uploaded) && !uploading && !saving && !refusal && !partial

	return (
		<CustomModal
			size="lg"
			opened={opened}
			onClose={onClose}
			title="Replace the English Word file"
		>
			<Stack spacing={16} sx={{ padding: 20 }}>
				<Stack spacing={2}>
					<Text weight={700}>
						{book?.title || '-'}
						{number ? ` · Chapter ${number}` : ''}
					</Text>
					<Text size="sm" color="dimmed">
						Current file: {current.url ? current.name : 'none'}
					</Text>
				</Stack>

				<WordFileReplaceNotice />

				<Stack spacing={8}>
					<Text size="sm" weight={600}>
						New file
					</Text>
					{progress > 0 && progress < 100 && <CustomProgress value={progress} />}
					{uploaded && (
						<Flex align="center" gap={6}>
							<IconFileTypeDocx size={18} color="#1f5fbf" />
							<Text size="sm" weight={600} lineClamp={1}>
								{uploaded.name}
							</Text>
						</Flex>
					)}
					<Dropzone
						accept={DOCX_DROPZONE_ACCEPT}
						onDrop={handleUpload}
						onReject={(rejections) =>
							void handleUpload(rejections.map((item) => item.file))
						}
						multiple={false}
						loading={uploading}
						sx={{
							border: '1px dashed #dbe6dc',
							borderRadius: 12,
							background: '#fafcfa',
							padding: 14,
						}}
					>
						<Text size="sm" color="dimmed" align="center">
							{uploaded
								? 'Drag and drop another DOCX file or click to browse'
								: 'Drag and drop the corrected DOCX file or click to browse'}
						</Text>
					</Dropzone>
				</Stack>

				{refusal && <WordFileRefusalAlert refusal={refusal} />}
				{partial && <WordFilePartialResult result={partial} />}

				<Flex justify="flex-end" gap={8}>
					<CustomButton
						variant="outlined"
						onClick={onClose}
						sx={{ height: 36, minWidth: 100 }}
					>
						{partial ? 'Close' : 'Cancel'}
					</CustomButton>
					{!partial && (
						<CustomButton
							variant="outlined"
							onClick={handleReplace}
							disabled={!canReplace}
							sx={{ height: 36, minWidth: 160 }}
						>
							{saving ? 'Replacing...' : 'Replace the Word file'}
						</CustomButton>
					)}
				</Flex>
			</Stack>
		</CustomModal>
	)
}

export default ReplaceWordFileDialog
